import { Components } from '@/api/generated/api';
import { toRecord } from 'common/utils/array.utils';
import { computed, Ref } from 'vue';

interface ICompetenceObservationRelationsComposable {
  getSchoolSubjectColor: (schoolSubjectId: number) => string | undefined;
  getSchoolSubjectColorForObservation: (observation: Components.Schemas.CompetenceObservationDto) => string | undefined;
  getCompetenceDomainForObservation: (
    observation: Components.Schemas.CompetenceObservationDto,
  ) => Components.Schemas.CompetenceDomainDto | null;
  getCompetenceForObservation: (
    observation: Components.Schemas.CompetenceObservationDto,
  ) => Components.Schemas.CompetenceDto | null;
  getSchoolSubjectById: (schoolSubjectId: number) => Components.Schemas.SchoolSubjectDto | undefined;
  getSchoolSubjectForObservation: (
    observation: Components.Schemas.CompetenceObservationDto,
  ) => Components.Schemas.SchoolSubjectDto | null;
  getSubjectAndDomainForObservation: (observation: Components.Schemas.CompetenceObservationDto) => {
    schoolSubject: Components.Schemas.SchoolSubjectDto | null;
    competenceDomain: Components.Schemas.CompetenceDomainDto | null;
  };
  getCompetenceDomainSymbolForObservation: (
    observation: Components.Schemas.CompetenceObservationDto,
  ) => string | undefined;

  getStudentForObservation: (
    observation: Components.Schemas.CompetenceObservationDto,
  ) => Components.Schemas.StudentWithSubjectAccessDto | null;
}

export const useCompetenceObservationRelations = (params: {
  availableCompetenceDomains: Ref<Components.Schemas.CompetenceDomainDto[]>;
  availableSchoolSubjects: Ref<Components.Schemas.SchoolSubjectDto[]>;
  availableStudents?: Ref<Components.Schemas.StudentWithSubjectAccessDto[]>;
}): ICompetenceObservationRelationsComposable => {
  const { availableCompetenceDomains, availableSchoolSubjects, availableStudents } = params;

  const getSchoolSubjectColor = (schoolSubjectId: number): string | undefined => {
    return availableSchoolSubjects.value.find((s) => s.id === schoolSubjectId)?.color;
  };

  const competenceById = computed<Partial<Record<number, Components.Schemas.CompetenceDto>>>(() => {
    const competences = availableCompetenceDomains.value.flatMap((domain) => domain.competences);
    return toRecord(
      competences,
      (competence) => competence.id,
      (competence) => competence,
    );
  });

  const competenceDomainById = computed<Partial<Record<number, Components.Schemas.CompetenceDomainDto>>>(() => {
    return toRecord(
      availableCompetenceDomains.value,
      (domain) => domain.id,
      (domain) => domain,
    );
  });

  const schoolSubjectById = computed<Partial<Record<number, Components.Schemas.SchoolSubjectDto>>>(() =>
    toRecord(
      availableSchoolSubjects.value,
      (s) => s.id,
      (s) => s,
    ),
  );

  const studentById = computed<Partial<Record<number, Components.Schemas.StudentWithSubjectAccessDto>>>(() =>
    availableStudents?.value
      ? toRecord(
          availableStudents.value,
          (student) => student.id,
          (student) => student,
        )
      : {},
  );

  const getSchoolSubjectById = (schoolSubjectId: number): Components.Schemas.SchoolSubjectDto | undefined =>
    schoolSubjectById.value[schoolSubjectId];

  const getSchoolSubjectColorForObservation = (
    observation: Components.Schemas.CompetenceObservationDto,
  ): string | undefined => {
    const schoolSubjectId = getCompetenceDomainForObservation(observation)?.schoolSubjectId;
    return schoolSubjectId ? getSchoolSubjectColor(schoolSubjectId) : undefined;
  };

  const getCompetenceForObservation = (
    observation: Components.Schemas.CompetenceObservationDto,
  ): Components.Schemas.CompetenceDto | null => {
    const competence = competenceById.value[observation.competenceId];
    return competence ?? null;
  };

  const getCompetenceDomainForObservation = (
    observation: Components.Schemas.CompetenceObservationDto,
  ): Components.Schemas.CompetenceDomainDto | null => {
    const competence = getCompetenceForObservation(observation);
    const domain = competence ? competenceDomainById.value[competence.domainId] : null;
    return domain ?? null;
  };

  const getSchoolSubjectForObservation = (
    observation: Components.Schemas.CompetenceObservationDto,
  ): Components.Schemas.SchoolSubjectDto | null => {
    const domain = getCompetenceDomainForObservation(observation);
    const sid = domain?.schoolSubjectId ?? null;
    return sid ? (schoolSubjectById.value[sid] ?? null) : null;
  };

  const getSubjectAndDomainForObservation = (observation: Components.Schemas.CompetenceObservationDto) => ({
    schoolSubject: getSchoolSubjectForObservation(observation),
    competenceDomain: getCompetenceDomainForObservation(observation),
  });

  const getCompetenceDomainSymbolForObservation = (
    observation: Components.Schemas.CompetenceObservationDto,
  ): string | undefined => getCompetenceDomainForObservation(observation)?.symbol;

  const getStudentForObservation = (
    observation: Components.Schemas.CompetenceObservationDto,
  ): Components.Schemas.StudentWithSubjectAccessDto | null => {
    if (!availableStudents?.value) return null;
    const student = studentById.value[observation.studentId];
    return student ?? null;
  };

  return {
    getCompetenceDomainForObservation,
    getCompetenceForObservation,
    getSchoolSubjectColor,
    getSchoolSubjectColorForObservation,
    getSchoolSubjectById,
    getSchoolSubjectForObservation,
    getSubjectAndDomainForObservation,
    getCompetenceDomainSymbolForObservation,
    getStudentForObservation,
  };
};
